/**
 * Zustand store for SVG template editor state management
 * Provides bidirectional state sync between React UI and canvas
 *
 * Supports buffered editing: mutations only update local state.
 * DB persistence is deferred to an explicit saveAll() call.
 */

import { create } from 'zustand';
import type { TemplateShape, ProductTemplate, ProductVariation, ShapeProcessSettings } from '@jigflow/shared';
import type { LightSourceMode } from '@jigflow/shared/generators/xcsGenerator';

export type CursorMode = 'select' | 'move' | 'draw';

/** Diff result returned by getChanges() */
export interface SnapshotChanges {
    shapes: {
        added: TemplateShape[];
        modified: TemplateShape[];
        deleted: string[]; // shape IDs
    };
    settings: {
        added: ShapeProcessSettings[];
        modified: ShapeProcessSettings[];
        deleted: string[]; // setting IDs
    };
    templateChanged: boolean;
}

interface TemplateStore {
    // State
    shapes: TemplateShape[];
    selectedShapeId: string | null;
    template: ProductTemplate | null;
    variations: ProductVariation[];
    selectedVariationId: string | null;
    cursorMode: CursorMode;
    zoom: number;
    showAlignmentGuides: boolean;
    snapEnabled: boolean;
    gridEnabled: boolean;
    loading: boolean;
    clipboard: Omit<TemplateShape, 'id' | 'templateId'>[] | null;

    // Snapshot state (for buffered editing)
    initialShapes: TemplateShape[];
    initialProcessSettings: Map<string, ShapeProcessSettings[]>; // keyed by shapeId
    initialTemplate: ProductTemplate | null;

    // Shape Actions
    setShapes: (shapes: TemplateShape[]) => void;
    addShape: (shape: TemplateShape) => void;
    updateShape: (id: string, updates: Partial<TemplateShape>) => void;
    deleteShape: (id: string) => void;
    selectShape: (id: string | null) => void;
    reorderShapes: (oldIndex: number, newIndex: number) => void;

    // Template Actions
    setTemplate: (template: ProductTemplate | null) => void;

    // Variation Actions
    setVariations: (variations: ProductVariation[]) => void;
    addVariation: (variation: ProductVariation) => void;
    updateVariation: (id: string, updates: Partial<ProductVariation>) => void;
    deleteVariation: (id: string) => void;
    selectVariation: (id: string | null) => void;

    // UI Actions
    setCursorMode: (mode: CursorMode) => void;
    setZoom: (zoom: number) => void;
    setShowAlignmentGuides: (show: boolean) => void;
    setSnapEnabled: (enabled: boolean) => void;
    setGridEnabled: (enabled: boolean) => void;
    setLoading: (loading: boolean) => void;

    // Clipboard Actions
    copyShapes: (shapes: TemplateShape[]) => void;
    getClipboard: () => Omit<TemplateShape, 'id' | 'templateId'>[] | null;
    clearClipboard: () => void;

    // Process Settings Panel
    processSettingsPanelExpanded: boolean;
    setProcessSettingsPanelExpanded: (expanded: boolean) => void;
    copiedProcessSettings: { operation: string; settings: ShapeProcessSettings } | { all: Map<string, ShapeProcessSettings> } | null;
    setCopiedProcessSettings: (settings: { operation: string; settings: ShapeProcessSettings } | { all: Map<string, ShapeProcessSettings> } | null) => void;
    activeProcessTab: 'score' | 'engrave' | 'cut';
    setActiveProcessTab: (tab: 'score' | 'engrave' | 'cut') => void;

    // Light source mode for multi-laser machines
    lightSourceMode: LightSourceMode;
    setLightSourceMode: (mode: LightSourceMode) => void;

    // Snapshot Actions (buffered editing)
    captureSnapshot: (processSettings?: Map<string, ShapeProcessSettings[]>) => void;
    getChanges: (currentProcessSettings: Map<string, ShapeProcessSettings[]>) => SnapshotChanges;
    hasUnsavedChanges: (currentProcessSettings: Map<string, ShapeProcessSettings[]>) => boolean;

    // Helpers
    getSelectedShape: () => TemplateShape | null;
    reset: () => void;
}

const initialState = {
    shapes: [] as TemplateShape[],
    selectedShapeId: null as string | null,
    template: null as ProductTemplate | null,
    variations: [] as ProductVariation[],
    selectedVariationId: null as string | null,
    cursorMode: 'select' as CursorMode,
    zoom: 1,
    showAlignmentGuides: true,
    snapEnabled: true,
    gridEnabled: false,
    loading: true,
    clipboard: null as Omit<TemplateShape, 'id' | 'templateId'>[] | null,
    processSettingsPanelExpanded: false,
    copiedProcessSettings: null as { operation: string; settings: ShapeProcessSettings } | { all: Map<string, ShapeProcessSettings> } | null,
    activeProcessTab: 'score' as 'score' | 'engrave' | 'cut',
    lightSourceMode: 'blue' as LightSourceMode,
    // Snapshot defaults
    initialShapes: [] as TemplateShape[],
    initialProcessSettings: new Map<string, ShapeProcessSettings[]>(),
    initialTemplate: null as ProductTemplate | null,
};

/** Deep-clone shapes array */
function cloneShapes(shapes: TemplateShape[]): TemplateShape[] {
    return JSON.parse(JSON.stringify(shapes));
}

/** Deep-clone process settings map */
function cloneSettingsMap(map: Map<string, ShapeProcessSettings[]>): Map<string, ShapeProcessSettings[]> {
    const cloned = new Map<string, ShapeProcessSettings[]>();
    for (const [key, value] of map) {
        cloned.set(key, JSON.parse(JSON.stringify(value)));
    }
    return cloned;
}

/** Compare two shapes for equality (ignoring reference identity) */
function shapeChanged(a: TemplateShape, b: TemplateShape): boolean {
    return JSON.stringify(a) !== JSON.stringify(b);
}

/** Compare two process settings for equality */
function settingChanged(a: ShapeProcessSettings, b: ShapeProcessSettings): boolean {
    return JSON.stringify(a) !== JSON.stringify(b);
}

export const useTemplateStore = create<TemplateStore>((set, get) => ({
    ...initialState,

    // Shape Actions
    setShapes: (shapes) => set({ shapes }),

    addShape: (shape) => set((state) => ({
        shapes: [...state.shapes, shape]
    })),

    updateShape: (id, updates) => set((state) => ({
        shapes: state.shapes.map(s => s.id === id ? { ...s, ...updates } : s)
    })),

    deleteShape: (id) => set((state) => ({
        shapes: state.shapes.filter(s => s.id !== id),
        selectedShapeId: state.selectedShapeId === id ? null : state.selectedShapeId
    })),

    selectShape: (id) => set({ selectedShapeId: id }),

    reorderShapes: (oldIndex, newIndex) => set((state) => {
        const shapes = [...state.shapes];
        const [removed] = shapes.splice(oldIndex, 1);
        shapes.splice(newIndex, 0, removed);

        const reindexed = shapes.map((shape, index) => ({
            ...shape,
            orderIndex: index,
        }));

        return { shapes: reindexed };
    }),

    // Template Actions
    setTemplate: (template) => set({ template }),

    // Variation Actions
    setVariations: (variations) => set({ variations }),

    addVariation: (variation) => set((state) => ({
        variations: [...state.variations, variation]
    })),

    updateVariation: (id, updates) => set((state) => ({
        variations: state.variations.map(v => v.id === id ? { ...v, ...updates } : v)
    })),

    deleteVariation: (id) => set((state) => ({
        variations: state.variations.filter(v => v.id !== id),
        selectedVariationId: state.selectedVariationId === id ? null : state.selectedVariationId
    })),

    selectVariation: (id) => set({ selectedVariationId: id }),

    // UI Actions
    setCursorMode: (mode) => set({ cursorMode: mode }),
    setZoom: (zoom) => set({ zoom }),
    setShowAlignmentGuides: (show) => set({ showAlignmentGuides: show }),
    setSnapEnabled: (enabled) => set({ snapEnabled: enabled }),
    setGridEnabled: (enabled) => set({ gridEnabled: enabled }),
    setLoading: (loading) => set({ loading }),

    // Clipboard Actions
    copyShapes: (shapes) => {
        const clipboardData = shapes.map(({ id, templateId, ...rest }) => rest);
        set({ clipboard: clipboardData });
    },
    getClipboard: () => get().clipboard,
    clearClipboard: () => set({ clipboard: null }),

    // Process Settings Panel
    setProcessSettingsPanelExpanded: (expanded) => set({ processSettingsPanelExpanded: expanded }),
    setCopiedProcessSettings: (settings) => set({ copiedProcessSettings: settings }),
    setActiveProcessTab: (tab) => set({ activeProcessTab: tab }),

    // Light source mode
    setLightSourceMode: (mode) => set({ lightSourceMode: mode }),

    // Snapshot Actions
    captureSnapshot: (processSettings) => {
        const state = get();
        set({
            initialShapes: cloneShapes(state.shapes),
            initialTemplate: state.template ? JSON.parse(JSON.stringify(state.template)) : null,
            initialProcessSettings: processSettings ? cloneSettingsMap(processSettings) : new Map(),
        });
    },

    getChanges: (currentProcessSettings) => {
        const state = get();
        const { initialShapes, initialTemplate, initialProcessSettings } = state;

        // Shape diff
        const currentShapeMap = new Map(state.shapes.map(s => [s.id, s]));
        const initialShapeMap = new Map(initialShapes.map(s => [s.id, s]));

        const addedShapes: TemplateShape[] = [];
        const modifiedShapes: TemplateShape[] = [];
        const deletedShapeIds: string[] = [];

        // Find added and modified shapes
        for (const [id, shape] of currentShapeMap) {
            const initial = initialShapeMap.get(id);
            if (!initial) {
                addedShapes.push(shape);
            } else if (shapeChanged(shape, initial)) {
                modifiedShapes.push(shape);
            }
        }

        // Find deleted shapes
        for (const [id] of initialShapeMap) {
            if (!currentShapeMap.has(id)) {
                deletedShapeIds.push(id);
            }
        }

        // Process settings diff
        // Flatten both maps to a list keyed by (shapeId + operation)
        const flattenSettings = (map: Map<string, ShapeProcessSettings[]>) => {
            const flat = new Map<string, ShapeProcessSettings>();
            for (const [, settings] of map) {
                for (const s of settings) {
                    flat.set(`${s.shapeId}:${s.operation}`, s);
                }
            }
            return flat;
        };

        const currentFlat = flattenSettings(currentProcessSettings);
        const initialFlat = flattenSettings(initialProcessSettings);

        const addedSettings: ShapeProcessSettings[] = [];
        const modifiedSettings: ShapeProcessSettings[] = [];
        const deletedSettingIds: string[] = [];

        for (const [key, setting] of currentFlat) {
            const initial = initialFlat.get(key);
            if (!initial) {
                addedSettings.push(setting);
            } else if (settingChanged(setting, initial)) {
                modifiedSettings.push(setting);
            }
        }

        for (const [key, setting] of initialFlat) {
            if (!currentFlat.has(key)) {
                deletedSettingIds.push(setting.id);
            }
        }

        // Template diff
        const templateChanged = initialTemplate && state.template
            ? JSON.stringify(state.template) !== JSON.stringify(initialTemplate)
            : (!initialTemplate && !!state.template) || (!!initialTemplate && !state.template);

        return {
            shapes: { added: addedShapes, modified: modifiedShapes, deleted: deletedShapeIds },
            settings: { added: addedSettings, modified: modifiedSettings, deleted: deletedSettingIds },
            templateChanged: !!templateChanged,
        };
    },

    hasUnsavedChanges: (currentProcessSettings) => {
        const changes = get().getChanges(currentProcessSettings);
        return (
            changes.shapes.added.length > 0 ||
            changes.shapes.modified.length > 0 ||
            changes.shapes.deleted.length > 0 ||
            changes.settings.added.length > 0 ||
            changes.settings.modified.length > 0 ||
            changes.settings.deleted.length > 0 ||
            changes.templateChanged
        );
    },

    // Helpers
    getSelectedShape: () => {
        const state = get();
        return state.shapes.find(s => s.id === state.selectedShapeId) || null;
    },

    reset: () => set({
        ...initialState,
        initialProcessSettings: new Map(),
    }),
}));
